"use client";

import { Button } from "@/components/ui/button";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useQuestAnalytics } from "@/context/AnalyticsProvider/quests";
import { CrownIcon, Filter } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { Header } from "../TilesSection/Header";
import { QuestFilterPanel } from "./QuestFilterPanel";
import { useQuestFilter } from "./QuestFilterProvider";
import { QuestFilterResults } from "./QuestFilterResults";

export function QuestFilterLayout() {
  const { isMobile, setIsMobile, gameGroups, filteredQuests } =
    useQuestFilter();
  const { track } = useQuestAnalytics();
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (hasViewedRef.current) return;
    hasViewedRef.current = true;
    track({
      action: Action.VIEWED,
      extras: {
        id: "quest-finder",
      },
    });
  }, [track]);

  // Prevent body scroll when mobile filter is open
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile]);

  const QuestFilterHeader = () => {
    return (
      <Header
        showDivider={false}
        title="Do Quests. $60,000 USD worth of IMX is up for grabs every week."
        description={
          <>
            The more Weekly Points{" "}
            <span className="inline-block">
              <CrownIcon className="w-4 h-4" />
            </span>{" "}
            you earn, the higher your chances! Questions?{" "}
            <Link href="/rewards/faq/" target="_blank">
              Read the FAQ.
            </Link>
          </>
        }
        testId="quest-filter"
      />
    );
  };

  const QuestFilterButton = () => {
    return (
      <Button
        onClick={() => {
          setIsMobile(true);
          track({
            action: Action.PRESSED,
            extras: {
              id: "quest-finder",
              control: "OpenFilterMobile",
              controlType: ControlType.BUTTON,
            },
          });
        }}
        size="default"
        className="hover:bg-white/90 flex-shrink-0 w-full"
      >
        <Filter className="w-4 h-4 mr-2" />
        Filters
      </Button>
    );
  };

  return (
    <>
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/onboarding/onboarding-background.webp"
          alt="Onboarding background"
          fill
          className="object-cover object-center"
          priority
        />
      </div>

      <div className="relative z-5">
        {/* Desktop/Tablet: Centered modal-like container with background */}

        <div className="flex flex-col gap-6">
          <QuestFilterHeader />

          {/* Desktop Layout - Two Columns */}
          <div className="hidden lg:flex gap-8 items-start">
            <div className="w-[360px] flex-shrink-0 sticky top-4">
              <QuestFilterPanel />
            </div>

            <div className="flex-1">
              <QuestFilterResults />
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="flex items-start justify-between gap-4 mb-4">
              <QuestFilterButton />
            </div>

            <QuestFilterResults />

            {/* Full Screen Filter Modal */}
            {isMobile && (
              <div className="fixed inset-0 z-50 bg-neutral-950 overflow-y-auto">
                <div className="px-4 mb-16">
                  <QuestFilterPanel
                    onClose={() => {
                      setIsMobile(false);
                      track({
                        action: Action.PRESSED,
                        extras: {
                          id: "quest-finder",
                          control: "CloseFilterMobile",
                          controlType: ControlType.BUTTON,
                        },
                      });
                    }}
                    showCloseButton
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
